/**
 * API Service - Handles communication with the chat backend.
 *
 * Tenant authority is derived server-side from the widget key. The widget
 * MUST NOT send organization_id, store_id, or any other tenant identifier.
 */

import {
  ApiResponse,
  ChatResponse,
  Message,
  CartItem,
  ChatEnvelope,
  EnvelopeMessage,
  EnvelopeAction,
  NormalizedChatPayload,
  Product,
  CartSummaryData,
  CheckoutEmbedData,
  WalletAddCardData,
  OrderStatusData,
  ProductGridData,
  ProductCardData,
  OpeningData,
  HostTransport,
} from '../types';

const WIDGET_KEY_HEADER = 'X-Lumiari-Widget-Key';
const WIDGET_SESSION_HEADER = 'X-Widget-Session-Token';
// Logged-in shopper identity token forwarded by the host storefront. Sent
// ONLY on a widget-key bootstrap request so chat_ms can validate it and
// seal the resolved user_id into the minted widget-session JWT. Treated as
// a bearer secret — never logged, never placed in the request body.
const SHOPPER_TOKEN_HEADER = 'X-Lumiari-Shopper-Token';
const GUEST_ID_STORAGE_PREFIX = 'lumiari_chat_guest_';
// Pre-v3 prefix — read ONLY by the one-time guest-id migration in
// `loadOrCreateGuestId()`. Drop once the migration window closes.
const LEGACY_GUEST_ID_STORAGE_PREFIX = 'tg_chat_guest_';

export class ApiService {
  private baseUrl: string;
  private storeId: string; // Retained for legacy widget UX (storage namespacing); never sent on chat requests.
  private agentId: string;
  private widgetKey: string | null;
  // Storefront-forwarded shopper identity token. Sent as the
  // X-Lumiari-Shopper-Token header on widget-key bootstrap requests only.
  // Bearer secret — never logged, never echoed into a request body.
  private shopperToken: string | null;
  private conversationHistory: Array<{ role: string; content: string }> = [];
  private conversationId: string | null = null;
  private guestId: string;
  /**
   * @internal Host transport (`transport: 'host'`, see `widget-host.js`).
   * When set, every `request` goes to the parent page instead of the network
   * and `requestRaw` refuses to run. Unset ⇒ plain HTTP, exactly as before.
   */
  tx?: HostTransport;
  /**
   * @internal Attribution suffix for the chat/opening requests:
   * `?platform=<tag>` when `init({ platform })` passed a valid tag, else ''.
   * A query parameter (not a body field or header) on purpose: the chat body
   * is `extra="forbid"` server-side and a custom header would fail the CORS
   * preflight, while FastAPI ignores unknown query parameters.
   */
  q = '';

  constructor(
    baseUrl: string,
    storeId: string,
    agentId: string = 'default',
    widgetKey: string | null = null,
    shopperToken: string | null = null
  ) {
    this.baseUrl = baseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.storeId = storeId || widgetKey || 'lumiari_widget';
    this.agentId = agentId;
    this.widgetKey = widgetKey;
    this.shopperToken = shopperToken || null;
    this.guestId = this.loadOrCreateGuestId();
  }

  /**
   * Set the agent ID
   */
  setAgentId(agentId: string): void {
    this.agentId = agentId;
  }

  /**
   * Set the widget key (server-issued public key). Required for the chat endpoint.
   */
  setWidgetKey(widgetKey: string | null): void {
    this.widgetKey = widgetKey;
  }

  private loadOrCreateGuestId(): string {
    const key = `${GUEST_ID_STORAGE_PREFIX}${this.storeId}`;
    const legacyKey = `${LEGACY_GUEST_ID_STORAGE_PREFIX}${this.storeId}`;
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const cached = window.localStorage.getItem(key);
        if (cached) {
          window.localStorage.removeItem(legacyKey); // clear stale pre-v3 slot
          return cached;
        }
        // One-time silent migration of the pre-v3 guest id — keeps the
        // shopper's server-side guest identity across the v3 rename.
        const legacy = window.localStorage.getItem(legacyKey);
        if (legacy) {
          window.localStorage.setItem(key, legacy);
          window.localStorage.removeItem(legacyKey);
          return legacy;
        }
      }
    } catch {
      // localStorage may be unavailable (CSP, private mode); fall through to memory id.
    }
    const fresh = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(key, fresh);
      }
    } catch {
      // Ignore storage write failure — id still works for the lifetime of the page.
    }
    return fresh;
  }

  private scopedStorageKey(key: string): string {
    return `lumiari:chat:${this.widgetKey || this.storeId}:${key}`;
  }

  private getWidgetSessionToken(): string | null {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return null;
      return window.localStorage.getItem(this.scopedStorageKey('widget_session_jwt'));
    } catch {
      return null;
    }
  }

  private setWidgetSessionToken(token: string | null): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      const key = this.scopedStorageKey('widget_session_jwt');
      if (token) {
        window.localStorage.setItem(key, token);
      } else {
        window.localStorage.removeItem(key);
      }
    } catch {
      // Storage unavailable — the next request can bootstrap with the widget key.
    }
  }

  private getShopperToken(): string | null {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return null;
      const raw = window.localStorage.getItem(this.scopedStorageKey('shopper_token'));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { token?: string; expires_at?: number };
      if (!parsed.token || !parsed.expires_at || Date.now() > parsed.expires_at) return null;
      return parsed.token;
    } catch {
      return null;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requiresWidgetKey = false
  ): Promise<ApiResponse<T>> {
    // Host transport: the parent page runs the turn (no fetch, no headers).
    if (this.tx) return this.tx.request(endpoint, options);

    const buildHeaders = (): { headers: Record<string, string>; sessionToken: string | null } => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...((options.headers as Record<string, string>) || {}),
      };
      const sessionToken = this.getWidgetSessionToken();
      if (sessionToken) {
        headers.Authorization = `Bearer ${sessionToken}`;
      } else if (this.widgetKey) {
        headers[WIDGET_KEY_HEADER] = this.widgetKey;
        // Bootstrap path only: forward the logged-in shopper identity so
        // chat_ms can seal user_id into the minted widget-session JWT. Once
        // a session token exists we authenticate with it (Authorization) and
        // never resend the shopper token — identity is already sealed.
        if (this.shopperToken) {
          headers[SHOPPER_TOKEN_HEADER] = this.shopperToken;
        }
      }
      return { headers, sessionToken };
    };

    const parseJson = async (response: Response): Promise<any> => {
      try {
        return await response.json();
      } catch {
        return {};
      }
    };

    const perform = async (retriedAfter401: boolean): Promise<ApiResponse<T>> => {
      const { headers, sessionToken } = buildHeaders();
      if (requiresWidgetKey && !this.widgetKey && !sessionToken) {
        return {
          success: false,
          error: 'Widget key not configured',
        };
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      const rotatedSession = response.headers?.get?.(WIDGET_SESSION_HEADER);
      if (rotatedSession) {
        this.setWidgetSessionToken(rotatedSession);
      }

      if (
        response.status === 401 &&
        sessionToken &&
        this.widgetKey &&
        !retriedAfter401
      ) {
        this.setWidgetSessionToken(null);
        return perform(true);
      }

      const data = await parseJson(response);

      if (!response.ok) {
        return {
          success: false,
          error: data.detail || data.message || 'Request failed',
        };
      }

      return {
        success: true,
        data: data.data || data,
      };
    };

    try {
      return await perform(false);
    } catch (error) {
      console.error('[LumiariChat] API Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Send a message to the agent.
   *
   * The endpoint returns either:
   *  - The legacy markdown shape (`{ response, products, cart_items, ... }`)
   *  - The new typed `ChatResponseEnvelope` (`{ version, messages, actions, ... }`).
   *
   * Both are projected into a single internal {@link NormalizedChatPayload}
   * shape so callers don't branch on transport.
   */
  async sendMessage(
    message: string,
    _sessionId: string | null
  ): Promise<ApiResponse<NormalizedChatPayload>> {
    // Add user message to conversation history
    this.conversationHistory.push({
      role: 'user',
      content: message,
    });

    const response = await this.request<ChatResponse | ChatEnvelope>(
      `/api/agents/${this.agentId}/chat${this.q}`,
      {
        method: 'POST',
        body: JSON.stringify({
          message,
          guest_id: this.guestId,
          shopper_token: this.getShopperToken(),
          conversation_id: this.conversationId,
        }),
      },
      true // requires widget key
    );

    if (!response.success || !response.data) {
      return { success: response.success, error: response.error };
    }

    const normalized = ApiService.normalizeChatPayload(response.data);

    // Store conversation_id for subsequent messages
    if (normalized.conversation_id) {
      this.conversationId = normalized.conversation_id;
    }

    // Add assistant response to history (use joined text content)
    if (normalized.response) {
      this.conversationHistory.push({
        role: 'assistant',
        content: normalized.response,
      });
    }

    return { success: true, data: normalized };
  }

  async sendToolIntent(intent: {
    tool?: string;
    type?: string;
    args?: Record<string, any>;
    payload?: Record<string, any>;
    args_schema_ref?: string;
    client_dispatch_id?: string;
    idempotency_key?: string;
  }): Promise<ApiResponse<NormalizedChatPayload>> {
    const body = {
      tool_intent: {
        type: intent.type,
        payload: intent.payload,
        idempotency_key: intent.idempotency_key,
        tool: intent.tool,
        args: intent.args,
        args_schema_ref: intent.args_schema_ref || intent.tool || intent.type,
        client_dispatch_id: intent.client_dispatch_id,
      },
      guest_id: this.guestId,
      shopper_token: this.getShopperToken(),
      conversation_id: this.conversationId,
    };
    const response = await this.request<ChatResponse | ChatEnvelope>(
      `/api/agents/${this.agentId}/chat${this.q}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      true,
    );

    if (!response.success || !response.data) {
      return { success: response.success, error: response.error };
    }

    const normalized = ApiService.normalizeChatPayload(response.data);
    if (normalized.conversation_id) {
      this.conversationId = normalized.conversation_id;
    }
    if (normalized.response) {
      this.conversationHistory.push({
        role: 'assistant',
        content: normalized.response,
      });
    }
    return { success: true, data: normalized };
  }

  /**
   * Set / clear the storefront shopper identity token.
   */
  setShopperToken(shopperToken: string | null): void {
    this.shopperToken = shopperToken || null;
  }

  /**
   * True when a sealed widget-session JWT already exists in storage.
   * Used by the follow-up flow to know whether identity has been
   * established (the session JWT carries the sealed user_id claim).
   */
  hasWidgetSession(): boolean {
    return !!this.getWidgetSessionToken();
  }

  /**
   * Start shopper OTP login. assistant-ms proxies to customers-ms and emails a
   * 6-digit code. The organization is injected server-side from the widget
   * context — the browser never sends it (anti-tenant-takeover).
   */
  async shopperLoginStart(
    email: string,
  ): Promise<ApiResponse<{ success: boolean; sent: boolean; reason?: string | null }>> {
    return this.request(
      `/api/agents/${this.agentId}/shopper-login/start`,
      { method: 'POST', body: JSON.stringify({ email }) },
      true, // requires widget key or an existing session
    );
  }

  /**
   * Verify the OTP code. On success the server re-mints the widget-session with
   * the sealed `user_id` and returns it in the `X-Widget-Session-Token` header;
   * the `request` wrapper stores it automatically, so the shopper is logged in
   * and subsequent chat turns unlock their orders / identity data.
   */
  async shopperLoginVerify(
    email: string,
    code: string,
  ): Promise<
    ApiResponse<{
      success: boolean;
      user?: { user_id?: string; name?: string | null; email?: string | null };
      reason?: string;
    }>
  > {
    return this.request(
      `/api/agents/${this.agentId}/shopper-login/verify`,
      { method: 'POST', body: JSON.stringify({ email, code }) },
      true,
    );
  }

  /**
   * Log the shopper out: drop the sealed widget-session (the next turn bootstraps
   * a fresh anonymous one from the widget key) and clear any forwarded shopper
   * token. Does not touch the conversation id.
   */
  logout(): void {
    this.setWidgetSessionToken(null);
    this.shopperToken = null;
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(this.scopedStorageKey('shopper_token'));
      }
    } catch {
      /* storage unavailable — nothing to clear */
    }
  }

  /**
   * Read the sealed `user_id` from the stored widget-session JWT to restore the
   * logged-in UI after a reload. Read-only decode of the base64url payload — the
   * server already verified the signature; we never trust this for auth.
   */
  getSessionUserId(): string | null {
    const tok = this.getWidgetSessionToken();
    if (!tok) return null;
    try {
      const part = tok.split('.')[1];
      if (!part) return null;
      const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
      const json = JSON.parse(
        decodeURIComponent(
          atob(b64)
            .split('')
            .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
            .join(''),
        ),
      );
      const uid = json?.user_id;
      return typeof uid === 'string' && uid ? uid : null;
    } catch {
      return null;
    }
  }

  // `getPendingFollowup()` / `ackPendingFollowup()` called
  // `/api/agents/{id}/pending-followup[/{id}/ack]`, which no live service
  // serves — and their PRODUCER no longer exists either: live-ms hands the
  // post-live cohort to the automations-ms campaign engine as a DRAFT
  // campaign (live-ms `src/services/post_live_followup_service.py`), never to
  // the widget. Unreachable UX shipped in every storefront bundle; removed in
  // VESNA-8 (2026-10-03).

  /**
   * Fetch the agent's opening greeting (A3b).
   *
   * The agent ALWAYS greets first. The widget calls this once on first open
   * (when the conversation has no messages yet) and renders the returned
   * `messages[]` as the first assistant bubble(s).
   *
   * Auth reuses the standard widget wiring (session JWT, else widget-key
   * bootstrap). Fully fail-safe: any non-200 / transport error returns
   * `{ success: false }` and the caller renders nothing.
   */
  async getOpening(): Promise<ApiResponse<OpeningData | null>> {
    if (!this.widgetKey && !this.getWidgetSessionToken()) {
      return { success: true, data: null };
    }
    try {
      const response = await this.requestRaw(
        `/api/agents/${this.agentId}/opening${this.q}`,
        { method: 'GET' },
      );
      if (response.status === 204) {
        return { success: true, data: null };
      }
      if (!response.ok) {
        return { success: false, error: `opening ${response.status}` };
      }
      let body: any = null;
      try {
        body = await response.json();
      } catch {
        return { success: true, data: null };
      }
      // Tolerate both the wrapped `{ data: { messages } }` envelope and a
      // bare `{ messages }` body.
      const data = body && typeof body === 'object'
        ? (body.data && typeof body.data === 'object' ? body.data : body)
        : null;
      const messages = data && Array.isArray(data.messages) ? data.messages : [];
      if (messages.length === 0) {
        return { success: true, data: null };
      }
      return {
        success: true,
        data: {
          display_name: typeof data.display_name === 'string' ? data.display_name : undefined,
          messages,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Low-level fetch used by the opening endpoint. Unlike `request()`,
   * the caller inspects the raw `Response` (it needs to distinguish 204
   * from 200). It applies the SAME auth header wiring (session JWT or
   * widget-key + shopper-token bootstrap) and forwards a rotated session
   * token, but does not normalize the JSON envelope.
   */
  private async requestRaw(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<Response> {
    // Host transport: the parent page owns the network. Callers catch this.
    if (this.tx) throw new Error('host transport');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };
    const sessionToken = this.getWidgetSessionToken();
    if (sessionToken) {
      headers.Authorization = `Bearer ${sessionToken}`;
    } else if (this.widgetKey) {
      headers[WIDGET_KEY_HEADER] = this.widgetKey;
      if (this.shopperToken) {
        headers[SHOPPER_TOKEN_HEADER] = this.shopperToken;
      }
    }
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });
    const rotatedSession = response.headers?.get?.(WIDGET_SESSION_HEADER);
    if (rotatedSession) {
      this.setWidgetSessionToken(rotatedSession);
    }
    return response;
  }

  /**
   * Project either a legacy `ChatResponse` or a typed `ChatEnvelope`
   * into the widget's internal `NormalizedChatPayload`.
   *
   * Detection: presence of an array `messages` (envelope contract requires
   * `version: "1"` + non-empty `messages`). Anything else falls back to
   * the legacy shape — preserving full backward compatibility.
   */
  static normalizeChatPayload(
    raw: ChatResponse | ChatEnvelope | Record<string, any>
  ): NormalizedChatPayload {
    if (raw && Array.isArray((raw as ChatEnvelope).messages)) {
      return ApiService.normalizeEnvelope(raw as ChatEnvelope);
    }
    return ApiService.normalizeLegacy(raw as ChatResponse);
  }

  private static normalizeEnvelope(env: ChatEnvelope): NormalizedChatPayload {
    const textParts: string[] = [];
    const products: Product[] = [];
    let cart: CartSummaryData | undefined;
    let checkoutEmbed: CheckoutEmbedData | undefined;
    let walletAddCard: WalletAddCardData | undefined;
    let orderStatus: OrderStatusData | undefined;
    let handoffContent: string | undefined;
    let loginRequest: { provider?: string; reason?: string; prompt?: string } | undefined;

    for (const msg of env.messages || []) {
      if (!msg) continue;
      switch (msg.type) {
        case 'text': {
          if (msg.content) textParts.push(msg.content);
          break;
        }
        case 'product_grid': {
          const grid = (msg.data || {}) as ProductGridData;
          const list = Array.isArray(grid.products) ? grid.products : [];
          for (const p of list) {
            products.push(ApiService.productCardToLegacy(p));
          }
          break;
        }
        case 'cart_summary': {
          cart = msg.data as CartSummaryData | undefined;
          break;
        }
        case 'checkout_embed': {
          checkoutEmbed = msg.data as CheckoutEmbedData | undefined;
          break;
        }
        case 'wallet_add_card': {
          walletAddCard = msg.data as WalletAddCardData | undefined;
          break;
        }
        case 'order_status': {
          orderStatus = msg.data as OrderStatusData | undefined;
          // Surface a textual fallback if no text messages were included.
          if (!msg.content && orderStatus?.order_id) {
            // No-op: the widget will render the order status block directly.
          } else if (msg.content) {
            textParts.push(msg.content);
          }
          break;
        }
        case 'auth_request': {
          loginRequest = (msg.data || {}) as { provider?: string; reason?: string; prompt?: string };
          if (msg.content) textParts.push(msg.content);
          break;
        }
        case 'handoff_notice': {
          if (msg.content) handoffContent = msg.content;
          break;
        }
        case 'action_request': {
          if (msg.content) textParts.push(msg.content);
          break;
        }
        default: {
          if (msg.content) textParts.push(msg.content);
        }
      }
    }

    if (handoffContent && textParts.length === 0) {
      textParts.push(handoffContent);
    }

    return {
      response: textParts.join('\n\n'),
      products,
      cart,
      checkout_embed: checkoutEmbed,
      wallet_add_card: walletAddCard,
      order_status: orderStatus,
      login_request: loginRequest,
      phase: env.phase,
      conversation_id: env.conversation_id,
      requires_human: !!env.requires_human,
      trace_id: env.trace_id,
      realtime_token: env.realtime_token,
      intent: env.intent,
      actions: Array.isArray(env.actions) ? env.actions : [],
      envelope_messages: Array.isArray(env.messages) ? env.messages : [],
      response_type: 'text',
      is_envelope: true,
      error_code: env.error?.code,
      retry_after_seconds: env.error?.retry_after_seconds ?? undefined,
    };
  }

  private static normalizeLegacy(data: ChatResponse): NormalizedChatPayload {
    return {
      response: data?.response || '',
      products: Array.isArray(data?.products) ? data.products : [],
      phase: data?.phase,
      conversation_id: data?.conversation_id,
      requires_human: !!data?.requires_human,
      session_id: data?.session_id,
      response_type: data?.response_type || 'text',
      response_options: data?.response_options,
      cart_items: data?.cart_items,
      cart_total: data?.cart_total,
      actions: [],
      is_envelope: false,
    };
  }

  private static productCardToLegacy(p: ProductCardData): Product {
    const id = p.product_id || '';
    const price = typeof p.price === 'number' ? p.price : 0;
    const currency = p.currency || '';
    const formattedAmount = Number.isFinite(price) ? price.toLocaleString('es-AR') : String(price);
    const formatted = currency ? `${currency} ${formattedAmount}` : `$${formattedAmount}`;
    return {
      id,
      name: p.name || '',
      price,
      price_formatted: formatted,
      image: p.image_url || '',
      url: p.url || '',
      description: p.description,
      stock: typeof p.stock === 'number' ? p.stock : undefined,
    };
  }

  // `listConversations()` called `GET /api/agents/{id}/conversations`, which
  // assistant-ms never implemented: it 404'd in production and the history
  // panel ALWAYS fell back to the current conversation. Removed in VESNA-8
  // (2026-10-03); the panel shows the current conversation, which is exactly
  // what shoppers saw. Reviving it needs the route in assistant-ms first
  // (sealed by widget_session) — `api.contract.test.ts` will demand it.

  /**
   * Get conversation ID
   */
  getConversationId(): string | null {
    return this.conversationId;
  }

  setConversationId(conversationId: string | null): void {
    this.conversationId = conversationId;
  }

  /**
   * Get the persistent guest identifier for this widget session.
   * The backend treats this as untrusted input — it never carries tenant authority.
   */
  getUserId(): string {
    return this.guestId;
  }

  /**
   * Clear conversation history
   */
  clearHistory(): void {
    this.conversationHistory = [];
    this.conversationId = null;
  }

  // `getSessionState()` y `clearSession()` vivían acá y pegaban contra
  // `/api/sales-agent/test/{sessionId}[/state]`: el arnés de test del
  // sales-agent de chat_ms. chat-ms está apagado (0/0 réplicas, Ingress
  // pruneado) y assistant-ms, su sucesor, nunca publicó ese arnés — censado el
  // 18/08/2026, 0 implementaciones en toda la plataforma. Dentro del widget
  // tampoco los llamaba nadie fuera de su propia definición. Se quitaron: era
  // el arnés de un servicio muerto viajando en el bundle de cada storefront.
  // El guard está en `api.rutas-sin-servidor.test.ts`.

  // `searchProducts`, `getProduct`, `addToCart`, `getCart` and
  // `createPaymentLink` called `/api/products/*`, `/api/cart/*` and
  // `/api/payments/create-link` on agent-api — routes assistant-ms never
  // served (the agent reaches catalog and cart through commerce-mcp) — and had
  // no caller in the widget. Removed in VESNA-8 (2026-10-03). The guard is
  // `api.contract.test.ts`: every route this client can call must exist in
  // assistant-ms.
}
